'use client';

import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DayTabs } from './day-tabs';
import { MealPlanCard } from './meal-plan-card';
import { MealSwapSheet } from './meal-swap-sheet';
import { PlanOverviewTab } from './plan-overview-tab';
import { TabNavigation } from './tab-navigation';
import type { Suggestion } from '@/api/use-get-same-item-planner';
import { trackEvent } from '@/lib/event-tracking';
import { useAIPlannerStore, type PlannedMeal } from '@/store/ai-planner';

type Tab = 'menu' | 'overview';

export function PlanOverview() {
  const currentPlan = useAIPlannerStore.use.currentPlan();
  const streamingMessages = useAIPlannerStore.use.streamingText();
  const updateMealStatus = useAIPlannerStore.use.updateMealStatus();
  const startRegeneration = useAIPlannerStore.use.startRegeneration();
  const nextStep = useAIPlannerStore.use.nextStep();

  const [activeTab, setActiveTab] = useState<Tab>('menu');
  const [visibleDate, setVisibleDate] = useState<string>('');
  const [swapMeal, setSwapMeal] = useState<{
    meal: PlannedMeal;
    date: string;
  } | null>(null);
  const dayRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Flatten all days from all weeks
  const allDays = useMemo(() => {
    if (!currentPlan?.weeks?.length) return [];
    return currentPlan.weeks.flatMap((week) => week.days);
  }, [currentPlan]);

  // Get plan info
  const planInfo = useMemo(() => {
    if (!currentPlan?.weeks?.length) return null;
    const firstWeek = currentPlan.weeks[0];
    const lastWeek = currentPlan.weeks[currentPlan.weeks.length - 1];
    return {
      startDate: firstWeek.startDate,
      endDate: lastWeek.endDate,
      totalWeeks: currentPlan.weeks.length,
    };
  }, [currentPlan]);

  // Set initial visible date
  useEffect(() => {
    if (!visibleDate && allDays.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todayOrFuture = allDays.find((day) => {
        const dayDate = new Date(day.date);
        dayDate.setHours(0, 0, 0, 0);
        return dayDate >= today;
      });

      setVisibleDate(todayOrFuture?.date ?? allDays[0].date);
    }
  }, [allDays, visibleDate]);

  // Setup IntersectionObserver for auto-scrolling tabs
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const date = entry.target.getAttribute('data-date');
            if (date) {
              setVisibleDate(date);
            }
          }
        });
      },
      {
        threshold: [0, 0.5, 1],
        rootMargin: '-20% 0px -20% 0px',
      }
    );

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  // Observe day sections
  useEffect(() => {
    const observer = observerRef.current;
    if (!observer) return;

    const currentRefs = dayRefsMap.current;
    currentRefs.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      currentRefs.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, [allDays]);

  // Scroll to date when DayTab is clicked
  const handleDateSelect = useCallback((date: string) => {
    const element = dayRefsMap.current.get(date);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, []);

  // // Meal actions
  // const handleOrder = useCallback(
  //   (meal: PlannedMeal) => {
  //     // Convert to menu item format and open cart sheet
  //     const menuItem = {
  //       _id: meal.itemId,
  //       name: meal.name,
  //       image_url: meal.imageUrl,
  //       price: meal.priceMin,
  //       discounted_price: meal.priceMin,
  //       store_id: meal.storeId,
  //       store_name: meal.storeName,
  //       customizations: [],
  //     };

  //     openCartSheet(menuItem as any, {
  //       storeId: String(meal.storeId),
  //       pageId: String(meal.storeId),
  //       slug: meal.storeSlug,
  //       branchId: '',
  //       tag: 'ai-planner',
  //       metadata: {
  //         source: 'ai_planner',
  //         meal_id: meal.id,
  //       },
  //     });

  //     // Navigate to store
  //     router.push(routes.storeDetail(meal.storeSlug));
  //   },
  //   [openCartSheet, router, routes]
  // );

  const handleCancel = useCallback(
    (date: string, meal: PlannedMeal) => {
      updateMealStatus(date, String(meal.id ?? 0), 'cancelled');
    },
    [updateMealStatus]
  );

  // const handleViewDetail = useCallback(
  //   (meal: PlannedMeal) => {
  //     // Navigate to store detail
  //     router.push(routes.storeDetail(meal.storeSlug));
  //   },
  //   [router, routes]
  // );

  const handleSwape = useCallback((meal: PlannedMeal, date: string) => {
    setSwapMeal({ meal, date });
  }, []);

  const handleSwapConfirm = useCallback(
    (newMeal: Suggestion) => {
      if (!swapMeal) return;

      // Update the meal in the plan
      // TODO: Implement updateMeal action in store
      console.log('Swapping meal:', swapMeal.meal.name, 'with:', newMeal.name);

      setSwapMeal(null);
    },
    [swapMeal]
  );

  const handleRegenerate = useCallback(() => {
    trackEvent('meal_plan', {
      button_name: 'meal_plan_regenerate',
    });
    // Clear plan and go back to onboarding with regeneration flag
    startRegeneration();
  }, [startRegeneration]);

  const handleNotificationSubscribe = useCallback(() => {
    trackEvent('meal_plan', {
      button_name: 'meal_plan_continue_to_reminder',
    });
    nextStep();
  }, [nextStep]);

  if (!currentPlan) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
        <p className="text-white/60 text-center mb-4">
          Chưa có kế hoạch ăn nào
        </p>
        <Button
          onClick={handleRegenerate}
          className="bg-[#ff8200] hover:bg-[#ff8200]/90 text-white"
        >
          Tạo kế hoạch mới
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full !scrollbar-hide">
      {/* Tab Navigation */}
      <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      {streamingMessages ? (
        <div className="flex w-full justify-between items-start mb-3 gap-2 ai-planner-button p-2 rounded-xl">
          <div className="size-7 relative aspect-square">
            <Image
              src={`${assetPrefix}/static/icons/ai-gen.svg`}
              alt="planner background"
              fill
              className="object-contain"
            />
          </div>
          <span className="text-xs text-white">{streamingMessages}</span>
        </div>
      ) : null}

      {/* Tab Content */}
      {activeTab === 'overview' ? (
        <PlanOverviewTab onEditPlan={handleRegenerate} />
      ) : (
        <div className="relative flex-1 flex flex-col !scrollbar-hide">
          {/* Plan Header */}
          <div
            className={cn(
              'sticky top-0 z-10 mb-4 -mx-4 px-4 py-3 transition-all duration-300 bg-[#012749]'
            )}
          >
            <div className="mb-1">
              <h2 className="text-white font-bold text-lg mb-1">
                Thực đơn ăn trưa của bạn
              </h2>
              {planInfo && (
                <p className="text-white/60 text-sm">
                  {format(new Date(planInfo.startDate), 'dd/MM', {
                    locale: vi,
                  })}{' '}
                  {'-'}{' '}
                  {format(new Date(planInfo.endDate), 'dd/MM/yyyy', {
                    locale: vi,
                  })}
                </p>
              )}
            </div>
            <DayTabs
              days={allDays}
              selectedDate={visibleDate}
              onSelectDate={handleDateSelect}
            />
          </div>

          {/* Vertical Timeline */}
          <ScrollArea
            className="flex-1 -mx-4 max-h-[calc(100dvh-123px-88px-24px)] px-4 overflow-auto !scrollbar-hide"
            ref={scrollAreaRef}
          >
            <div className="relative pb-6 h-full">
              {allDays.map((day) => {
                const dayDate = new Date(day.date);

                return (
                  <div
                    key={day.date}
                    ref={(el) => {
                      if (el) dayRefsMap.current.set(day.date, el);
                    }}
                    data-date={day.date}
                    className="relative mb-8 last:mb-0"
                  >
                    {/* Date Header */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="shrink-0">
                        <p className="text-white/40 text-xs uppercase">
                          {format(dayDate, 'EEEE', { locale: vi })}
                        </p>
                        <p className="text-white font-bold text-sm">
                          {format(dayDate, 'dd/MM/yyyy', { locale: vi })}
                        </p>
                      </div>
                    </div>

                    {/* Meals for this day */}
                    <div className="space-y-6 relative">
                      {/* Vertical timeline line */}
                      {/* {dayIndex < allDays.length - 1 && (
                        <div className="absolute left-[18px] inset-y-0 w-0.5 bg-gradient-to-b from-white/20 via-white/10 to-transparent" />
                      )} */}

                      {day.meals.map((meal, index) => {
                        // const StatusIcon =
                        //   meal.status === 'passed'
                        //     ? Check
                        //     : meal.status === 'cancelled'
                        //       ? X
                        //       : null;

                        return (
                          <div key={meal.id} className="relative">
                            <MealPlanCard
                              index={index}
                              meal={meal}
                              onSwape={() => handleSwape(meal, day.date)}
                              // onOrder={() => handleOrder(meal)}
                              onCancel={() => handleCancel(day.date, meal)}
                              // onViewDetail={() => handleViewDetail(meal)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="h-24" />
          </ScrollArea>

          {/* Footer Actions */}
          <div className="fixed bottom-0 inset-x-0 pb-4 pt-3 px-4 flex gap-3 bg-white/10 backdrop-blur-xl z-20 border-t rounded-t-3xl">
            <Button
              variant="outline"
              onClick={handleRegenerate}
              className="flex-1 h-14 rounded-xl ai-planner-button text-white border-none bg-white/5 font-medium text-sm"
            >
              Tạo menu mới
            </Button>
            <Button
              onClick={handleNotificationSubscribe}
              className="flex-1 h-14 rounded-xl ai-planner-button text-white font-medium text-sm"
            >
              Tiếp tục
            </Button>
          </div>
        </div>
      )}

      {/* Meal Swap Sheet */}
      <MealSwapSheet
        meal={swapMeal?.meal ?? null}
        date={swapMeal?.date ?? ''}
        slot={swapMeal?.meal?.slot ?? ''}
        isOpen={Boolean(swapMeal)}
        onClose={() => setSwapMeal(null)}
        onSwap={handleSwapConfirm}
      />
    </div>
  );
}
